import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { cn } from '@/lib/utils'
import { Wand2 } from 'lucide-react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { HintLabel, InfoTooltip } from './info-tooltip'
import { PlatformGroupHeader } from './platform-group-header'

type CraftSignal = RouterOutput['analytics']['captionCraft']['signals'][number]

export function CaptionCraftCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading } = trpc.analytics.captionCraft.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  const grouped = data?.signals ? groupByPlatform(data.signals) : new Map()
  const isMultiPlatform = grouped.size > 1

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Wand2 size={14} className="text-muted-foreground" />
          Caption Craft
          <InfoTooltip>
            How small caption choices — emoji, @ mentions, links — change engagement. Lift is
            calculated as posts <em>with</em> the element vs. posts <em>without</em> on the same
            platform.
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <SkeletonRows />}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            Need posts both with and without these caption elements on at least one platform.
          </p>
        )}

        {data && data.hasEnoughData && (
          <div className={cn(isMultiPlatform ? 'space-y-4' : 'space-y-3')}>
            {[...grouped.entries()].map(([platform, signals]) => (
              <div key={platform}>
                {isMultiPlatform && <PlatformGroupHeader platform={platform} />}
                <SignalList signals={signals} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function SignalList({ signals }: { signals: CraftSignal[] }) {
  return (
    <ul className="space-y-2">
      {signals.map((s) => {
        const liftPct = s.lift != null ? (s.lift * 100).toFixed(0) : null
        const liftSign = s.lift != null && s.lift >= 0 ? '+' : ''
        const liftClass =
          s.lift == null
            ? 'text-muted-foreground'
            : s.lift > 0
              ? 'text-emerald-600 dark:text-emerald-500'
              : 'text-rose-600 dark:text-rose-500'
        return (
          <li key={`${s.platform}-${s.id}`} className="flex items-center justify-between text-xs">
            <HintLabel hint={s.hint} className="text-foreground font-medium">
              {s.label}
            </HintLabel>
            <div className="flex items-baseline gap-2">
              <span className="text-muted-foreground/70 text-[10px]">
                {s.withCount} with · {s.withoutCount} without
              </span>
              <span className={cn('font-medium tabular-nums', liftClass)}>
                {liftPct != null ? `${liftSign}${liftPct}%` : '—'}
              </span>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center justify-between">
          <div className="bg-muted h-3 w-24 animate-pulse rounded" />
          <div className="bg-muted h-3 w-12 animate-pulse rounded" />
        </div>
      ))}
    </div>
  )
}

function groupByPlatform<T extends { platform: PlatformType }>(rows: T[]): Map<PlatformType, T[]> {
  const m = new Map<PlatformType, T[]>()
  for (const row of rows) {
    const list = m.get(row.platform) ?? []
    list.push(row)
    m.set(row.platform, list)
  }
  return m
}
